import React, { useMemo, useCallback } from "react";

import { Line } from "react-chartjs-2";
import {
	Chart as ChartJS,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	Filler,
} from "chart.js";

import { getFacetTheme } from "../assets/facetTheme";
import "../styling/Facets.css";

ChartJS.register(
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	Filler
);

function hexAlpha(hex, alpha) {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

function findMedian(curve) {
	for (let i = 1; i < curve.length; i++) {
		if (curve[i].survival <= 0.5) return curve[i].month;
	}
	return null;
}

function fmtPct(v) {
	return `${(v * 100).toFixed(1)}%`;
}

function makeMedianPlugin(medianMonth, primaryColor) {
	return {
		id: "medianLine",
		beforeDatasetsDraw(chart) {
			if (medianMonth == null) return;

			const { ctx, scales } = chart;
			const xScale = scales.x;
			const yScale = scales.y;

			const labels = chart.data.labels;
			const idx = labels.indexOf(String(medianMonth));
			if (idx === -1) return;

			const x = xScale.getPixelForValue(idx);
			const y05 = yScale.getPixelForValue(0.5);
			const yBottom = yScale.getPixelForValue(0);

			ctx.save();
			ctx.setLineDash([5, 4]);
			ctx.strokeStyle = primaryColor;
			ctx.lineWidth = 1.5;
			ctx.globalAlpha = 0.65;

			ctx.beginPath();
			ctx.moveTo(x, y05);
			ctx.lineTo(x, yBottom);
			ctx.stroke();

			const xLeft = xScale.getPixelForValue(0);

			ctx.beginPath();
			ctx.moveTo(xLeft, y05);
			ctx.lineTo(x, y05);
			ctx.stroke();

			ctx.restore();
		},
	};
}

const EMPTY_SELECTION = new Set();

export default function KaplanMeierFacet({
	data,
	loading,
	filters,
	setFilters,
	field,
	category,
}) {
	const theme = getFacetTheme(category);
	const PRIMARY = theme.primary;
	const CI_FILL = hexAlpha(PRIMARY, 0.12);
	const CI_BORDER = hexAlpha(PRIMARY, 0.35);


	const GRID = "#E9EDF2";
	const AXIS = "#97A3B6";
	const LABEL = "#4A5568";

	const sel = filters && field ? filters.get(field) || EMPTY_SELECTION : EMPTY_SELECTION;

	const handlePickMonth = useCallback(
		(month) => {
			if (!setFilters || !field) return;

			const val = String(month);

			setFilters((prev) => {
				const next = new Map(prev);
				const curr = new Set(next.get(field) || []);

				if (curr.has(val)) curr.delete(val);
				else curr.add(val);

				if (curr.size > 0) next.set(field, curr);
				else next.delete(field);

				return next;
			});
		},
		[setFilters, field]
	);

	const { curve, n, medianMonth } = useMemo(() => {
		if (!data) {
			return {
				curve: [],
				n: 0,
				medianMonth: null,
			};
		}

		const cappedCurve = (data.curve || []).filter((pt) => pt.month <= 24);

		return {
			curve: cappedCurve,
			n: data.n || 0,
			medianMonth: findMedian(cappedCurve),
		};
	}, [data]);

	const labels = useMemo(() => {
		return curve.map((pt) => String(pt.month));
	}, [curve]);

	const selectedIndices = useMemo(() => {
		if (!sel.size) return new Set();

		return new Set(
			labels
				.map((lab, i) => (sel.has(lab) ? i : -1))
				.filter((i) => i !== -1)
		);
	}, [labels, sel]);

	const chartData = useMemo(() => {
		if (!curve.length) {
			return {
				labels: [],
				datasets: [],
			};
		}

		return {
			labels,
			datasets: [
				{
					label: "95% CI Upper",
					data: curve.map((pt) => pt.upper ?? pt.survival),
					stepped: "before",
					borderColor: CI_BORDER,
					borderWidth: 0,
					pointRadius: 0,
					fill: "+1",
					backgroundColor: sel.size ? hexAlpha(PRIMARY, 0.05) : CI_FILL,
					tension: 0,
					order: 2,
				},
				{
					label: "95% CI Lower",
					data: curve.map((pt) => pt.lower ?? pt.survival),
					stepped: "before",
					borderColor: CI_BORDER,
					borderWidth: 0,
					pointRadius: 0,
					fill: false,
					backgroundColor: "transparent",
					tension: 0,
					order: 2,
				},
				{
					label: "Survival",
					data: curve.map((pt) => pt.survival),
					stepped: "before",
					borderColor: sel.size ? hexAlpha(PRIMARY, 0.4) : PRIMARY,
					borderWidth: 2.5,
					pointRadius: (ctx) =>
						selectedIndices.has(ctx.dataIndex) ? 5 : 0,
					pointBackgroundColor: PRIMARY,
					pointHoverRadius: 6,
					pointHoverBackgroundColor: PRIMARY,
					fill: false,
					tension: 0,
					order: 0,
				},
			],
		};
	}, [
		curve,
		labels,
		selectedIndices,
		sel.size,
		PRIMARY,
		CI_FILL,
		CI_BORDER,
	]);

	const medianPlugin = useMemo(() => {
		return makeMedianPlugin(medianMonth, PRIMARY);
	}, [medianMonth, PRIMARY]);

	const chartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,

			animation: false,

			transitions: {
				active: {
					animation: {
						duration: 0,
					},
				},
			},

			layout: {
				padding: {
					top: 8,
					right: 12,
					bottom: 4,
					left: 4,
				},
			},

			/*
			onClick: (_evt, active) => {
				if (!active?.length) return;

				const pointIndex = active[0].index;
				const month = Number(labels[pointIndex]);

				if (month > 0) {
					handlePickMonth(month);
				}
			},

			onHover: (event, active) => {
				const target = event?.native?.target;
				if (!target) return;

				target.style.cursor = active?.length ? "pointer" : "default";
			},
			*/

			scales: {
				x: {
					type: "category",
					grid: {
						color: GRID,
						drawBorder: false,
					},
					ticks: {
						color: AXIS,
						maxRotation: 0,
						autoSkip: false,
						callback(value, index) {
							const month = Number(labels[index]);
							const lastMonth = Number(labels[labels.length - 1]);

							if (lastMonth < 12) return month;

							if (lastMonth < 24) {
								return month % 6 === 0 || month === lastMonth
									? month
									: "";
							}

							return month % 12 === 0 || month === lastMonth
								? month
								: "";
						},
					},
					title: {
						display: true,
						text: "Time (months)",
						color: LABEL,
						font: {
							size: 9,
						},
						padding: {
							top: 0,
						},
					},
				},

				y: {
					min: 0,
					max: 1,
					grid: {
						color: GRID,
						drawBorder: false,
					},
					ticks: {
						color: AXIS,
						stepSize: 0.2,
						callback: (v) => `${Math.round(v * 100)}%`,
					},
					title: {
						display: true,
						text: "Survival probability",
						color: LABEL,
						font: {
							size: 9,
						},
						padding: {
							bottom: 0,
						},
					},
				},
			},

			interaction: {
				mode: "nearest",
				intersect: false,
				axis: "x",
			},

			plugins: {
				legend: {
					display: false,
				},

				tooltip: {
					intersect: false,
					mode: "nearest",
					filter: (item) => item.datasetIndex === 2,

					callbacks: {
						title: ([ctx]) => {
							if (!ctx) return "";

							const month = Number(labels[ctx.dataIndex]);
							return `Month ${month}`;
						},

						label: (ctx) => {
							if (ctx.datasetIndex !== 2) return null;

							const pt = curve[ctx.dataIndex];
							if (!pt) return null;

							const lines = [`S(t): ${fmtPct(pt.survival)}`];

							if (pt.lower != null && pt.upper != null) {
								lines.push(
									`95% CI: ${fmtPct(pt.lower)} – ${fmtPct(
										pt.upper
									)}`
								);
							}

							if (pt.events != null) {
								lines.push(`Events: ${pt.events}`);
							}

							if (pt.atRisk != null) {
								lines.push(`At risk: ${pt.atRisk}`);
							}

							return lines;
						},

						labelColor: () => ({
							borderColor: PRIMARY,
							backgroundColor: PRIMARY,
						}),
					},
				},
			},
		}),
		[
			labels,
			curve,
			handlePickMonth,
			PRIMARY,
			GRID,
			AXIS,
			LABEL,
		]
	);

	if (loading) {
		return (
			<div className="facet-loading">
				<div className="spinner" />
			</div>
		);
	}

	if (!curve.length) {
		return <div className="facet-empty">No survival data available.</div>;
	}

	return (
		<div className="km-facet">
			<div className="chart-fill km">
				<Line
					data={chartData}
					options={chartOptions}
					plugins={[medianPlugin]}
				/>
			</div>
		</div>
	);
}